import React from "react";
import { useBookPreorderAdapter } from "../hooks/useBookPreorderAdapter";
import { PreorderExperience, type PreorderCompleteResult } from "../preOrders/PreorderExperience";
import type { PendingBooking, PreorderSelections } from "./BookingForm";
import type { PreorderMenu, CountryConfig, VenueConfig } from "../../../common/jsonRpcTypes";
import type { LinkedBooking } from "../context/PreorderContext";
import PageStateGuard from "../shared/PageStateGuard";

interface BookPreorderStageProps {
  /** The pending booking created during the /book flow. */
  readonly pendingBooking: PendingBooking | null;
  /** Preorder menus for the selected booking type. */
  readonly preorderMenus: PreorderMenu[];
  /** Linked booking types with their preorder menus. */
  readonly linkedBookings: LinkedBooking[] | null;
  /** Venue configuration from the create-pending-booking response. */
  readonly venueConfig: VenueConfig | null;
  /** Country configuration (currency, country code). */
  readonly countryConfig: CountryConfig | null;
  /** Service charge percentage from the selected booking type. */
  readonly serviceChargePercentage: number;
  /** Whether the venue hides the allergens and dietary controls. */
  readonly hideDietaryRequirements?: boolean;
  /**
   * Whether the selected booking type has the shareable pre-order link disabled,
   * in which case the multi-person ordering controls are hidden.
   */
  readonly disableShareableLink?: boolean;
  /** Deposit information - when present with a preorder, allows proceeding with empty basket */
  readonly depositRequired?: import("../../../common/jsonRpcTypes").DepositRequired;
  /**
   * Pre-order selections confirmed earlier in the flow, used to restore the
   * basket when the guest navigates back to this step (e.g. from payment).
   */
  readonly preorderSelections?: PreorderSelections | null;
  /** Widget heading slot the pre-order title portals into, as on the payment stages. */
  readonly headingSlot?: HTMLElement | null;
  /** Called when the user completes preorder selection. */
  readonly onComplete: (result: PreorderCompleteResult) => void | Promise<void>;
  /** Called when the user presses the back button. */
  readonly onBack: () => void;
  readonly onCacheExpired?: () => void;
  readonly onCacheExtended?: () => void;
  /**
   * Hides the "Place your order" CTA — set when the booking's outcome is
   * already unknown (a timed-out `/submitBooking`).
   */
  readonly submitHidden?: boolean;
}

/**
 * BookPreorderStage — wrapper component that bridges the /book route's
 * data model with PreorderExperience.
 *
 * Uses `useBookPreorderAdapter` to synthesise Booking and CustomerPreOrder
 * shapes from the pending booking and preorder menus, then renders
 * PreorderExperience with `showHeader=false` (parent manages navigation).
 */
const BookPreorderStage: React.FC<BookPreorderStageProps> = ({
  pendingBooking,
  preorderMenus,
  linkedBookings,
  venueConfig,
  countryConfig,
  serviceChargePercentage,
  hideDietaryRequirements,
  disableShareableLink,
  depositRequired,
  preorderSelections,
  headingSlot,
  onComplete,
  onBack,
  onCacheExpired,
  onCacheExtended,
  submitHidden = false,
}) => {
  const {
    bookingData,
    bookingId,
    initialCustomerPreOrder,
    defaultServiceChargePercentage,
    ready,
    linkedBookings: adapterLinkedBookings,
    bookingTypeName,
    bookingTypeId,
  } = useBookPreorderAdapter({
    pendingBooking,
    preorderMenus,
    linkedBookings,
    venueConfig,
    countryConfig,
    serviceChargePercentage,
    hideDietaryRequirements,
    restoredSelections: preorderSelections,
  });

  if (!ready || !bookingData) {
    return (
      <PageStateGuard loading={true} error={null} data={null}>
        {null}
      </PageStateGuard>
    );
  }

  return (
    <PreorderExperience
      bookingId={bookingId}
      bookingData={bookingData}
      onComplete={onComplete}
      onBack={onBack}
      onCacheExpired={onCacheExpired}
      onCacheExtended={onCacheExtended}
      showHeader={false}
      initialCustomerPreOrder={initialCustomerPreOrder}
      defaultServiceChargePercentage={defaultServiceChargePercentage}
      linkedBookings={adapterLinkedBookings}
      bookingTypeName={bookingTypeName}
      bookingTypeId={bookingTypeId}
      depositRequired={depositRequired}
      preorderMenus={preorderMenus}
      disableShareableLink={disableShareableLink}
      headingSlot={headingSlot}
      submitHidden={submitHidden}
    />
  );
};

export default BookPreorderStage;
export type { BookPreorderStageProps };
